import { useEffect, useState, useRef } from "react";
import { Button, Card, Col, Row, Spinner } from "reactstrap";
import useApiCallService from "../../Common/Helpers/ApiCall";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CustomButton } from "../../Common/Components/Button/CustomButton";
import { CustomSubmitButton } from "../../Common/Components/Input/CustomSubmitButton";
import { ADMIN_PATHS } from "../../config";
import { FlashMessage } from "../../Common/Helpers/FlashMessage";
import { useTranslation } from "react-i18next";
import { CheckUserPermission } from "../../AppSystem/Helpers/CheckUserPermission";
import { CustomModal } from "../../Common/Components/Modal/CustomModal";
import { CustomReload } from "../../AppSystem/Helpers/CustomReload";
import { useHeader } from "../../Auth/Components/Nav/HeaderContext";
import { CheckMobile } from "../../AppSystem/Helpers/CheckMobile";

const AdminGuideUpdate = () => {
    const { t } = useTranslation();
    const { ApiCall } = useApiCallService();
    const [searchParams] = useSearchParams();
    const id = searchParams.get('id') ?? "";
    const navigate = useNavigate();
    const isMobile = CheckMobile(998);
    const { setPageTitle, setPageTitleKey, setOnBack } = useHeader();

    useEffect(() => {
        setPageTitle(t('guide.update', 'Edit Guide'));
        setPageTitleKey('guide.update');
        setOnBack(() => { navigate(ADMIN_PATHS.ADMIN_GUIDE_LIST); });
        return () => { setPageTitle(''); setPageTitleKey(''); setOnBack(null); };
    }, [t]);

    const [guide, setGuide] = useState({ id: "", routeId: "", description: "", content: "", hasGuide: false });
    const [displaySpinner, setDisplaySpinner] = useState(false);
    const [displayDeleteModal, setDisplayDeleteModal] = useState(false);
    const [deleteApiResponse, setDeleteApiResponse] = useState(null);

    let permissionUpdate = false;
    if (
        CheckUserPermission("system.setting.guide.update") || 
        CheckUserPermission("system.setting.guide") || 
        CheckUserPermission("system.setting") || 
        CheckUserPermission("system")
    ) {
        permissionUpdate = true;
    }

    let permissionDelete = false;
    if (
        CheckUserPermission("system.setting.guide.delete") ||
        CheckUserPermission("system.setting.guide") ||
        CheckUserPermission("system.setting") ||
        CheckUserPermission("system")
    ) {
        permissionDelete = true;
    }

    // GET GUIDE BY ID
    useEffect(() => {
        if (!id) return;
        const fetchData = async () => {
            try {
                const apiCallOptions = {
                    method: "get",
                    url: `/guide/by-id?id=${encodeURIComponent(id)}`,
                };
                await ApiCall(apiCallOptions, (response) => {
                    if (response) setGuide(response);
                });
            } catch (error) {
                console.error(error);
            }
        };
        fetchData();
    }, [id]);

    const handleChange = (field, value) => {
        setGuide((prev) => ({ ...prev, [field]: value }));
    };

    // SAVE — PUT uses routeId (loaded from guide state)
    const handleSave = async () => {
        setDisplaySpinner(true);
        const apiCallOptions = {
            method: "put",
            url: `/guide?routeId=${encodeURIComponent(guide.routeId)}`,
            data: {
                id: guide.id,
                description: guide.description,
                content: guide.content ?? null,
            },
        };
        try {
            await ApiCall(apiCallOptions, (response) => {
                if (response) {
                    setDisplaySpinner(false);
                    FlashMessage(t('guide.updated', 'Guide updated'), 'flash-messageSuccess');
                    setTimeout(() => { navigate(ADMIN_PATHS.ADMIN_GUIDE_LIST); }, 2000);
                }
            });
        } catch (error) {
            console.error(error);
            FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
            setDisplaySpinner(false);
        }
    };

    // DELETE — DELETE uses routeId (loaded from guide state)
    const handleDelete = async () => {
        const apiCallOptions = {
            method: "delete",
            url: `/guide?routeId=${encodeURIComponent(guide.routeId)}`,
        };
        try {
            await ApiCall(apiCallOptions, setDeleteApiResponse);
            setDisplayDeleteModal(false);
        } catch (error) {
            console.error(error);
            FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
        }
    };

    useEffect(() => {
        if (deleteApiResponse !== null) {
            FlashMessage(t('guide.deleted', 'Guide deleted'), 'flash-messageSuccess');
            CustomReload(ADMIN_PATHS.ADMIN_GUIDE_LIST);
        }
    }, [deleteApiResponse]);

    const modalContentDelete = (
        <div>
            {t('validationMessage.modalDelete', 'Are you sure you want to delete this guide?')}
            <p className="mt-2 text-muted"><strong>Route:</strong> {guide.routeId}</p>
        </div>
    );

    const modalFooterDelete = (
        <>
            <Button className="box-green border-0" onClick={handleDelete}>
                {t('button.delete', 'Delete')}
            </Button>
            <Button className="box-red border-0" onClick={() => setDisplayDeleteModal(false)}>
                {t('button.cancel', 'Cancel')}
            </Button>
        </>
    );

    return (
        <>
            <CustomModal
                isOpen={displayDeleteModal}
                toggle={() => setDisplayDeleteModal(!displayDeleteModal)}
                body={modalContentDelete}
                footer={modalFooterDelete}
            />

            {!isMobile && (
                <Col lg={12}>
                    <CustomButton
                        text={t('button.back')}
                        icon={'bx bx-chevron-left'}
                        buttonSize={'small'}
                        customClasses={'ms-3 mb-3'}
                        onClick={() => navigate(ADMIN_PATHS.ADMIN_GUIDE_LIST)}
                    />
                </Col>
            )}

            <Row className="pb-4">
                <Col lg={6} className="mx-0 px-0">
                    <Card className="card-backend p-4">
                        <Row>
                            <Col lg={12}>
                                <h2 className="rokkitt-font">{t('guide.update', 'Edit Guide')}</h2>
                            </Col>
                            <Col lg={12}>
                                <Row>
                                    
                                    {/* Route — read-only, loaded from guide state */}
                                    <Col lg={12}>
                                        <div className="mb-3">
                                            <label className="form-label">
                                                {t('guide.routeId', 'Route')}
                                            </label>
                                            <input
                                                className="form-control"
                                                type="text"
                                                value={guide.routeId}
                                                disabled
                                            />
                                        </div>
                                    </Col>

                                    {/* Description */}
                                    <Col lg={12}>
                                        <div className="mb-3">
                                            <label className="form-label">
                                                {t('guide.description', 'Description')}
                                            </label>
                                            <input
                                                className="form-control"
                                                type="text"
                                                value={guide.description ?? ""}
                                                onChange={(e) => handleChange("description", e.target.value)}
                                                placeholder={t('guide.descriptionPlaceholder', 'Add a short description...')}
                                            />
                                        </div>
                                    </Col>

                                    {/* Has Guide checkbox */}
                                    {/* <Col lg={12}>
                                        <div className="form-check mb-3">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="hasGuideCheckbox"
                                                checked={!!guide.hasGuide}
                                                onChange={(e) => handleChange("hasGuide", e.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor="hasGuideCheckbox">
                                                {t('guide.hasGuide', 'Has guide content')}
                                            </label>
                                        </div>
                                    </Col> */}

                                    {/* Actions */}
                                    <Col lg={12} className="text-end pt-5">
                                        {permissionDelete && id !== "" && (
                                            <Button
                                                className="rounded-2 box-red border-0 me-2"
                                                onClick={() => setDisplayDeleteModal(true)}
                                            >
                                                <i className="bx bx-x-circle fs-5 pe-1" />
                                                {t('button.delete', 'Delete')}
                                            </Button>
                                        )}

                                        {displaySpinner && (
                                            <>
                                                {t('validationMessage.sending', 'Saving...')}{' '}
                                                <Spinner color='success' className="me-3" />
                                            </>
                                        )}

                                        {permissionUpdate && (
                                            <CustomSubmitButton
                                                text={t('button.update', 'Update')}
                                                id='submitGuide'
                                                icon='bx bx-save pe-2'
                                                customClick={handleSave}
                                            />
                                        )}
                                    </Col>

                                </Row>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default AdminGuideUpdate;